import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

interface Props {
  assignmentTitle: string;
  currentDueDate: Date;
  onClose: () => void;
  onSubmit: (dueDate: Date) => Promise<void>;
}

interface State {
  selectedDate?: Date;
  isSubmitting: boolean;
  errorMessage?: string;
}

class UpdateDueDateModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedDate: props.currentDueDate,
      isSubmitting: false,
      errorMessage: undefined,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (
      prevProps.assignmentTitle !== this.props.assignmentTitle ||
      prevProps.currentDueDate !== this.props.currentDueDate
    ) {
      this.setState({
        selectedDate: this.props.currentDueDate,
        errorMessage: undefined,
      });
    }
  }

  private handleChangeDueDate(date: Dayjs | null) {
    this.setState({
      selectedDate: date ? date.toDate() : undefined,
    });
  }

  private async handleSubmit() {
    if (!this.state.selectedDate) {
      this.setState({
        errorMessage: "Selecione uma nova data limite antes de salvar.",
      });
      return;
    }
    this.setState({
      isSubmitting: true,
      errorMessage: undefined,
    });
    try {
      await this.props.onSubmit(this.state.selectedDate);
    } catch (error: any) {
      const message =
        error?.message ||
        (typeof error === "string" ? error : "Não foi possível atualizar o prazo.");
      this.setState({
        errorMessage: message,
      });
    } finally {
      this.setState({
        isSubmitting: false,
      });
    }
  }

  private formatDate(date: Date) {
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    return (
      <Dialog open onClose={this.props.onClose} variant="modal" maxWidth="sm" fullWidth>
        <DialogTitle variant="modal">
          <EventRepeatIcon />
          Atualizar prazo da aplicação
        </DialogTitle>
        <DialogContent variant="modal">
          <Typography variant="subtitle1" fontWeight={500}>
            {this.props.assignmentTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Prazo atual: {this.formatDate(this.props.currentDueDate)}
          </Typography>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              ptBR.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <MobileDateTimePicker
              label="Novo prazo para conclusão"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={
                this.state.selectedDate ? dayjs(this.state.selectedDate) : null
              }
              onChange={(date) => this.handleChangeDueDate(date)}
              sx={{
                mt: 3,
                width: "100%",
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          {this.state.errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {this.state.errorMessage}
            </Alert>
          )}

        </DialogContent>
        <DialogActions variant="modal" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="error"
            onClick={this.props.onClose}
            disabled={this.state.isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => this.handleSubmit()}
            disabled={!this.state.selectedDate || this.state.isSubmitting}
          >
            {this.state.isSubmitting ? "Salvando..." : "Salvar novo prazo"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default UpdateDueDateModal;
